/**
 * CreditPill — TopNav credit balance + plan indicator.
 *
 * The right-most pill before the avatar. Always visible to authenticated
 * users. Source: useCredits hook (live via PB realtime + bumpCredits).
 *
 * Visual shape (v2.0.1 — three converged patterns from Vercel / Figma /
 * Linear / Cursor — see research notes):
 *
 *   Paid (Pro / Studio)
 *     ┌──────┬─────────────────┐
 *     │ PRO  │ ⚡ 500 / 500     │
 *     └──────┴─────────────────┘
 *      ↑plan ↑credits          ← single pill, two semantic chips
 *
 *   Free (healthy)
 *     ┌─────────────────────────┐
 *     │ ✨ 5 today              │
 *     └─────────────────────────┘
 *
 * Plan chip uses brand violet (consumer-SaaS convention — subtle, not
 * the loud Canva gold). Credit half uses the same severity treatments
 * we had in v2.0.0 (healthy / low / exhausted).
 *
 * Click → dropdown. Hover → preview popover with the same content.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, Zap, Crown, CreditCard, Loader2, AlertCircle,
  RefreshCw, Plus, ExternalLink, ArrowUpRight,
} from "lucide-react";
import { useCredits } from "@/hooks/useCredits";
import { useAuth } from "@/hooks/useAuth";
import {
  TOP_UP_PACK,
  getTopUpUrl,
  isBillingConfigured,
  openCheckout,
  creditsAsImagesLabel,
} from "@/lib/billing";
import { useToast } from "@/components/Toast";
import { ROUTES, type PlanTier } from "@/types";

type Severity = "healthy" | "low" | "exhausted" | "loading";

interface DisplayState {
  severity: Severity;
  /** The right-half text only ("500 / 500" or "5 today" etc.) — plan label is rendered separately. */
  primaryLabel: string;
  ariaLabel: string;
  remaining: number;
  total: number;
  isFree: boolean;
  monthlyExhausted: boolean;
}

export default function CreditPill() {
  const { data, loading, refresh } = useCredits();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Tween the count down/up on consumption so the deduction is visible
  // even when realtime delivers a sub-second update.
  const [displayed, setDisplayed] = useState<number | null>(null);
  const prevRef = useRef<number | null>(null);
  useEffect(() => {
    if (!data) return;
    const target = data.balance.remaining;
    const prev = prevRef.current;
    if (prev === null) { setDisplayed(target); prevRef.current = target; return; }
    if (prev === target) return;
    const start = performance.now();
    const duration = 320;
    let frame: number;
    const step = (t: number) => {
      const pct = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - pct, 3);
      const v = Math.round(prev + (target - prev) * eased);
      setDisplayed(v);
      if (pct < 1) frame = requestAnimationFrame(step);
      else { setDisplayed(target); prevRef.current = target; }
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [data?.balance.remaining]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const state: DisplayState | null = useMemo(() => {
    if (loading && !data) {
      return { severity: "loading", primaryLabel: "…", ariaLabel: "Loading credits", remaining: 0, total: 0, isFree: false, monthlyExhausted: false };
    }
    if (!data) return null;
    const remaining = displayed ?? data.balance.remaining;
    const total = data.balance.granted + (data.balance.bonusGranted || 0);
    const isFree = data.plan === "free";
    const monthlyExhausted = isFree && data.monthly.remaining <= 0;

    if (isFree) {
      if (monthlyExhausted) {
        return { severity: "exhausted", primaryLabel: "Out this month", ariaLabel: "Out of credits this month", remaining, total, isFree, monthlyExhausted };
      }
      if (remaining <= 0) {
        return { severity: "exhausted", primaryLabel: "Used today", ariaLabel: "Used your daily credits", remaining, total, isFree, monthlyExhausted };
      }
      if (remaining <= 2) {
        return { severity: "low", primaryLabel: `${remaining} today`, ariaLabel: `${remaining} of ${total} generations left today`, remaining, total, isFree, monthlyExhausted };
      }
      return { severity: "healthy", primaryLabel: `${remaining} today`, ariaLabel: `${remaining} of ${total} generations left today`, remaining, total, isFree, monthlyExhausted };
    }

    // Paid: render the explicit "X / Y" count so the user sees both the
    // remaining and the pool — Vercel / Figma convergence.
    const pct = total > 0 ? remaining / total : 0;
    const label = `${remaining.toLocaleString()} / ${total.toLocaleString()}`;
    if (remaining <= 0)       return { severity: "exhausted", primaryLabel: label, ariaLabel: `Out of generations · ${label}`, remaining, total, isFree, monthlyExhausted };
    if (pct < 0.20)           return { severity: "low",       primaryLabel: label, ariaLabel: `${label} generations left`, remaining, total, isFree, monthlyExhausted };
    return                      { severity: "healthy",   primaryLabel: label, ariaLabel: `${label} generations left`, remaining, total, isFree, monthlyExhausted };
  }, [data, loading, displayed]);

  if (!state) return null;

  const plan: PlanTier = data?.plan ?? "free";
  const isPaid = plan !== "free";

  const handleUpgrade = () => {
    setOpen(false);
    navigate(ROUTES.PLANS);
  };
  const handleTopUp = async () => {
    setOpen(false);
    const url = getTopUpUrl();
    if (!isBillingConfigured() || !url) {
      toast.info(
        "Billing not wired yet",
        "Top-ups go live once the LemonSqueezy store + checkout URLs are set on the server.",
      );
      return;
    }
    try {
      await openCheckout(url, {
        userId: user?.id || "",
        email: user?.email,
        onSuccess: () => { void refresh(); },
      });
    } catch (err) {
      toast.error("Couldn't open checkout", err instanceof Error ? err.message : "Please try again");
    }
  };

  const handleManageBilling = () => {
    setOpen(false);
    if (data?.manageUrl) {
      window.open(data.manageUrl, "_blank", "noopener,noreferrer");
    } else {
      // Fallback for legacy subs created before v2.0.1 captured the URL.
      navigate(ROUTES.PLANS);
    }
  };

  const v = getPillVisuals(state.severity);
  const planChipV = getPlanChipVisuals(plan);

  return (
    <div ref={wrapperRef} className="cl-credit-pill" style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((vv) => !vv)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={() => setHover(true)}
        onBlur={() => setHover(false)}
        aria-label={`${isPaid ? `${planLabel(plan)} plan · ` : ""}${state.ariaLabel}`}
        aria-expanded={open}
        style={{
          display: "inline-flex",
          alignItems: "stretch",
          height: 36,
          // The pill is one continuous shape composed of a plan half
          // (paid only) + credit half. Border + radius live on the outer
          // button so the two halves share the rounded shell.
          background: v.background,
          border: v.border,
          borderRadius: "var(--radius-pill)",
          fontSize: "var(--font-size-xs)",
          fontWeight: 600,
          fontFamily: "var(--font-family)",
          cursor: "pointer",
          transition: "transform 160ms var(--ease-smooth), background 160ms, border-color 160ms, box-shadow 160ms",
          boxShadow: v.boxShadow,
          position: "relative",
          overflow: "hidden",
          padding: 0,
        }}
      >
        {/* Plan half — only renders for paid users. Uses the brand
            gradient (purple/violet) to read as identity, not severity. */}
        {isPaid && (
          <span
            aria-hidden
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "0 10px",
              background: planChipV.background,
              color: planChipV.color,
              fontFamily: "var(--font-mono)",
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: "var(--tracking-label)",
              textTransform: "uppercase",
              boxShadow: planChipV.boxShadow,
            }}
          >
            {plan === "studio" ? <Crown size={9} strokeWidth={2.25} /> : <Zap size={9} strokeWidth={2.25} />}
            {planLabel(plan)}
          </span>
        )}

        {/* Credit half */}
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "0 12px",
          color: v.color,
        }}>
          {state.severity === "loading" ? (
            <Loader2 size={12} strokeWidth={1.5} className="animate-spin" style={{ color: "var(--text-tertiary)" }} />
          ) : state.severity === "exhausted" ? (
            <AlertCircle size={12} strokeWidth={1.75} />
          ) : state.severity === "low" ? (
            <Zap size={12} strokeWidth={1.75} />
          ) : (
            <Sparkles size={12} strokeWidth={1.75} />
          )}
          <span style={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
            {state.primaryLabel}
          </span>
        </span>

        {state.severity === "low" && (
          <span aria-hidden style={{
            position: "absolute",
            inset: -2,
            borderRadius: "inherit",
            background: "var(--status-warning)",
            opacity: 0.18,
            animation: "cl-pill-pulse 1800ms ease-in-out infinite",
            pointerEvents: "none",
          }} />
        )}
      </button>

      <PillDetail
        visible={hover && !open}
        anchor="hover"
        data={data}
        state={state}
        onUpgrade={handleUpgrade}
        onTopUp={handleTopUp}
        onManageBilling={handleManageBilling}
        onClose={() => {}}
      />

      {open && (
        <PillDetail
          visible={open}
          anchor="dropdown"
          data={data}
          state={state}
          onUpgrade={handleUpgrade}
          onTopUp={handleTopUp}
          onManageBilling={handleManageBilling}
          onClose={() => setOpen(false)}
        />
      )}

      <style>{`
        @keyframes cl-pill-pulse {
          0%, 100% { transform: scale(1);   opacity: 0.18; }
          50%      { transform: scale(1.06); opacity: 0.35; }
        }
      `}</style>
    </div>
  );
}

/* ── Visual treatments per severity (credit half) ────────────── */

function getPillVisuals(severity: Severity) {
  switch (severity) {
    case "healthy":
      return {
        background: "linear-gradient(135deg, rgba(255,107,157,0.10), rgba(197,63,255,0.08))",
        border: "1px solid color-mix(in srgb, var(--accent-300) 28%, transparent)",
        color: "var(--text-primary)",
        boxShadow: "0 0 0 1px rgba(255,255,255,0.04) inset, 0 6px 16px -10px rgba(255,77,109,0.4)",
      };
    case "low":
      return {
        background: "var(--status-warning-bg)",
        border: "1px solid color-mix(in srgb, var(--status-warning) 35%, transparent)",
        color: "var(--status-warning)",
        boxShadow: "0 6px 14px -10px rgba(245,158,11,0.4)",
      };
    case "exhausted":
      return {
        background: "var(--status-danger-bg)",
        border: "1px solid color-mix(in srgb, var(--status-danger) 35%, transparent)",
        color: "var(--status-danger)",
        boxShadow: "none",
      };
    case "loading":
    default:
      return {
        background: "var(--bg-surface-alt)",
        border: "1px solid var(--border-default)",
        color: "var(--text-tertiary)",
        boxShadow: "none",
      };
  }
}

/* ── Plan-chip treatments (left half — paid only) ────────────── */

function getPlanChipVisuals(plan: PlanTier) {
  if (plan === "studio") {
    return {
      background: "linear-gradient(135deg, #6D28D9, #C53FFF)",
      color: "#fff",
      boxShadow: "inset -1px 0 0 rgba(255,255,255,0.18)",
    };
  }
  // pro
  return {
    background: "var(--brand-gradient)",
    color: "#fff",
    boxShadow: "inset -1px 0 0 rgba(255,255,255,0.18)",
  };
}

function planLabel(plan: PlanTier): string {
  if (plan === "studio") return "Studio";
  if (plan === "pro")    return "Pro";
  return "Free";
}

/* ── Detail card (hover preview + click dropdown reuse) ──────── */

const PillDetail: React.FC<{
  visible: boolean;
  anchor: "hover" | "dropdown";
  data: ReturnType<typeof useCredits>["data"];
  state: DisplayState;
  onUpgrade: () => void;
  onTopUp: () => void;
  onManageBilling: () => void;
  onClose: () => void;
}> = ({ visible, anchor, data, state, onUpgrade, onTopUp, onManageBilling, onClose }) => {
  const navigate = useNavigate();
  if (!visible || !data) return null;

  const interactive = anchor === "dropdown";

  const total = data.balance.granted + (data.balance.bonusGranted || 0);
  const used  = data.balance.used;
  const remaining = Math.max(0, total - used);
  const fillPct = total > 0 ? Math.min(100, (used / total) * 100) : 0;
  const resetLabel = formatReset(data.balance.resetAt || data.balance.periodEnd, data.balance.resetCadence);
  const planName = planLabel(data.plan);
  const isPaid = data.plan !== "free";

  return (
    <div
      role={interactive ? "dialog" : "tooltip"}
      className="animate-dropdown-in"
      style={{
        position: "absolute",
        right: 0,
        top: "calc(100% + 8px)",
        width: 320,
        padding: 14,
        background: "var(--bg-surface-raised)",
        border: "1px solid var(--border-default)",
        borderRadius: "var(--radius-xl)",
        boxShadow: "var(--shadow-dropdown, 0 24px 60px -24px rgba(0,0,0,0.35))",
        zIndex: "var(--z-dropdown)" as unknown as number,
        pointerEvents: interactive ? "auto" : "none",
      }}
    >
      {/* Header — plan + reset */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 12,
      }}>
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          padding: "4px 10px",
          background: isPaid ? "var(--brand-gradient)" : "var(--bg-surface-alt)",
          color: isPaid ? "#fff" : "var(--text-tertiary)",
          borderRadius: "var(--radius-pill)",
          fontFamily: "var(--font-mono)",
          fontSize: 10, fontWeight: 700,
          textTransform: "uppercase", letterSpacing: "var(--tracking-label)",
        }}>
          {isPaid
            ? (data.plan === "studio" ? <Crown size={10} strokeWidth={2.25} /> : <Zap size={10} strokeWidth={2.25} />)
            : <Sparkles size={10} strokeWidth={2.25} />}
          {planName}
        </span>
        <span style={{
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          color: "var(--text-tertiary)",
          textTransform: "uppercase",
          letterSpacing: "var(--tracking-label)",
        }}>
          {resetLabel}
        </span>
      </div>

      {/* Big number */}
      <div style={{
        display: "flex", alignItems: "baseline", gap: 8,
        marginBottom: 6,
      }}>
        <span style={{
          fontFamily: "var(--font-display)",
          fontSize: 32, fontWeight: 600,
          letterSpacing: "var(--tracking-display-md)",
          color: state.severity === "exhausted" ? "var(--status-danger)"
               : state.severity === "low" ? "var(--status-warning)"
               : "var(--text-primary)",
          fontVariantNumeric: "tabular-nums",
        }}>
          {remaining.toLocaleString()}
        </span>
        <span style={{ fontSize: "var(--font-size-sm)", color: "var(--text-tertiary)" }}>
          of {total.toLocaleString()} {data.balance.resetCadence === "daily" ? "today" : "this month"}
        </span>
      </div>

      <div style={{
        position: "relative",
        height: 6,
        background: "var(--bg-surface-alt)",
        border: "1px solid var(--border-default)",
        borderRadius: "var(--radius-pill)",
        overflow: "hidden",
        marginBottom: 12,
      }}>
        <div style={{
          position: "absolute",
          left: 0, top: 0, bottom: 0,
          width: `${fillPct}%`,
          background: state.severity === "exhausted"
            ? "var(--status-danger)"
            : state.severity === "low"
            ? "var(--status-warning)"
            : "var(--brand-gradient)",
          transition: "width 320ms var(--ease-smooth)",
        }} />
      </div>

      {data.plan === "free" && (
        <div style={{
          display: "flex", justifyContent: "space-between",
          fontSize: 11,
          color: "var(--text-tertiary)",
          marginBottom: 12,
          fontFamily: "var(--font-mono)",
        }}>
          <span>This month</span>
          <span style={{ fontVariantNumeric: "tabular-nums" }}>
            {data.monthly.used} / {data.monthly.cap}
          </span>
        </div>
      )}

      {(data.balance.bonusGranted || 0) > 0 && (
        <div style={{
          padding: "8px 10px",
          background: "var(--accent-50)",
          border: "1px solid color-mix(in srgb, var(--accent-300) 25%, transparent)",
          borderRadius: "var(--radius-md)",
          fontSize: 11,
          color: "var(--accent-400)",
          marginBottom: 10,
          display: "flex", alignItems: "center", gap: 6,
        }}>
          <Plus size={11} strokeWidth={2} />
          +{data.balance.bonusGranted} bonus credits from top-up
        </div>
      )}

      {/* Renewal info — paid only. Tells the user exactly when the next
          charge / refresh lands. Linear / Vercel / Notion convention. */}
      {interactive && isPaid && data.currentPeriodEnd && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "8px 10px",
          background: "var(--bg-surface-alt)",
          borderRadius: "var(--radius-md)",
          fontSize: 11,
          color: "var(--text-secondary)",
          marginBottom: 10,
        }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <RefreshCw size={11} strokeWidth={1.75} style={{ color: "var(--accent-300)" }} />
            {data.cancelledAt ? "Ends" : "Renews"}
          </span>
          <span style={{ fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums", color: "var(--text-primary)" }}>
            {formatLongDate(data.currentPeriodEnd)}
          </span>
        </div>
      )}

      {interactive && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
          {data.plan === "free" ? (
            <button onClick={onUpgrade} style={primaryActionStyle}>
              <Zap size={13} strokeWidth={2} />
              Upgrade to Pro
            </button>
          ) : (
            <>
              <button onClick={onTopUp} style={primaryActionStyle}>
                <Plus size={13} strokeWidth={2} />
                +{TOP_UP_PACK.credits} for ${TOP_UP_PACK.priceUsd}
              </button>
              <button onClick={onManageBilling} style={secondaryActionStyle}>
                <ExternalLink size={13} strokeWidth={1.5} />
                Manage subscription
              </button>
            </>
          )}
          {data.plan === "free" && (
            <button onClick={() => { onClose(); navigate(ROUTES.PLANS); }} style={secondaryActionStyle}>
              <CreditCard size={13} strokeWidth={1.5} />
              View all plans
            </button>
          )}
        </div>
      )}

      <div style={{
        marginTop: 12,
        paddingTop: 10,
        borderTop: "1px solid var(--border-default)",
        fontSize: 10,
        color: "var(--text-tertiary)",
        fontFamily: "var(--font-mono)",
        textTransform: "uppercase",
        letterSpacing: "var(--tracking-label)",
        display: "flex", alignItems: "center", gap: 6,
      }}>
        <ArrowUpRight size={9} strokeWidth={1.75} />
        1 generation = 1 image or 1 edit
      </div>
    </div>
  );
};

/* ── Helpers ─────────────────────────────────────────────────── */

const primaryActionStyle: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
  width: "100%", height: 36,
  background: "var(--brand-gradient)",
  color: "#fff",
  border: "none",
  borderRadius: "var(--radius-md)",
  fontSize: "var(--font-size-sm)", fontWeight: 600,
  cursor: "pointer",
  boxShadow: "0 8px 22px -10px rgba(255,77,109,0.55)",
  transition: "transform 160ms var(--ease-smooth), box-shadow 160ms",
};

const secondaryActionStyle: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
  width: "100%", height: 32,
  background: "transparent",
  color: "var(--text-secondary)",
  border: "1px solid var(--border-default)",
  borderRadius: "var(--radius-md)",
  fontSize: "var(--font-size-xs)", fontWeight: 500,
  cursor: "pointer",
  transition: "var(--transition-fast)",
};

function formatReset(iso: string | null, cadence: "daily" | "monthly"): string {
  if (!iso) return cadence === "daily" ? "resets daily" : "resets monthly";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = d.getTime() - now.getTime();
    const diffH = Math.round(diffMs / (1000 * 60 * 60));
    if (cadence === "daily") {
      if (diffH <= 1) return "resets in <1h";
      if (diffH < 24) return `resets in ${diffH}h`;
      return "resets tomorrow";
    }
    const month = d.toLocaleString("en-GB", { month: "short" });
    return `renews ${d.getUTCDate()} ${month}`;
  } catch {
    return cadence === "daily" ? "resets daily" : "resets monthly";
  }
}

function formatLongDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch { return "—"; }
}

export { creditsAsImagesLabel };
